import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import { useSelector } from 'react-redux';

import PlayNowImage from '../../../assets/homeRedesign/hero/play-now.png';
import UserIcon from '../../../assets/homeRedesign/user-icon.png';
import { selectTotalActiveUsersCount } from '../../../store/lobby/lobbySagas';
import { formatBigNumbers } from '../../../utils/helpers/numberFormat';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { RootStackParamList } from '../../navigator';
import { useHeaderTopOffset } from '../../Pages/headerOffset';
import { getMobileJackpotBlockHeight } from '../../shell/header/TopJackpotBanner';
import { getNavRowHeight } from '../AppFooter/Nav/FooterNavButtonsV2';
import { getRecentTablesBarHeight, RecentTablesBar } from '../common/RecentTablesBar';
import { Pressable } from '../common/Touchable';
import { getHeaderPillScale, HEADER_PILL } from '../Top/headerBar';
import CategoryCard from './CategoryCard';
import { useHomeCards } from './homeCards';

type Nav = NativeStackNavigationProp<RootStackParamList>;

// The design leaves 7 between the recent-tables bar and the jackpot bar under it (the lobby
// stacks the same pair with the same gap).
const RECENT_BAR_TO_JACKPOT_GAP = 7;

// The design frame's Hero - the Play Now art, with the header's missions / free-chips pills on its
// top edge - sits at 114. The header draws those pills HEADER_PILL.top below its own top, so the
// hero follows the header wherever the layout hosts it: under the status-bar inset on native
// (47 on the frame's phone, 62 under a Dynamic Island), HeaderTopOffsetContext down on mobile web.
// Keyed off the 844-high frame the hero ignored all that, so on a notched phone the girl rode up
// into the top bar while the pills stayed under it.
const DESIGN_HERO_TOP = 114;
const DESIGN_CARDS_TOP = 335;

const HomeRedesignContent = ({ onPlayNowPress }: { onPlayNowPress: () => void }) => {
	const { t } = useTranslation();
	const { navigate } = useNavigation<Nav>();
	const { width, height, insets } = useSafeViewport();
	const headerTopOffset = useHeaderTopOffset();
	const onlineCount = useSelector(selectTotalActiveUsersCount);
	const horizontalScale = getHeaderPillScale(width);
	const verticalScale = height / 844;
	// AppFooter pins the jackpot bar above the nav, and the design puts the recent-tables bar
	// directly on top of that pair. Anchoring it to the footer instead of to a share of the
	// viewport height keeps the three together on any aspect ratio - keyed off the design frame
	// it drifted down a whole jackpot bar on a screen taller than 390x844 and disappeared behind
	// it.
	const footerStackBottom = width * (12 / 390);
	const infoBarsBottom =
		footerStackBottom +
		getNavRowHeight(width, height, true) +
		getMobileJackpotBlockHeight(width) +
		RECENT_BAR_TO_JACKPOT_GAP * horizontalScale;
	const heroTop = insets.top + headerTopOffset + HEADER_PILL.top;
	// The card grid keeps its distance from the hero, but never at the cost of running into the
	// recent-tables bar anchored above the footer.
	const cardScale = getCardScale(horizontalScale, width);
	const cardsGridHeight = 2 * 141 * cardScale + 7 * horizontalScale;
	const cardsTopLimit =
		height -
		infoBarsBottom -
		getRecentTablesBarHeight(360 * horizontalScale) -
		RECENT_BAR_TO_JACKPOT_GAP * horizontalScale -
		cardsGridHeight;
	const designCardsTop = DESIGN_CARDS_TOP * verticalScale;
	const cardsTop = Math.max(
		designCardsTop,
		Math.min(designCardsTop + heroTop - DESIGN_HERO_TOP * verticalScale, cardsTopLimit),
	);
	const styles = createStyles(horizontalScale, width, infoBarsBottom, heroTop, cardsTop);

	const cards = useHomeCards();

	return (
		<View pointerEvents='box-none' style={styles.root}>
			<View pointerEvents='box-none' style={styles.canvas}>
				<View style={styles.hero}>
					<Pressable onPress={onPlayNowPress} style={styles.playArea}>
						<Image source={PlayNowImage} style={styles.playImage} resizeMode='contain' />
					</Pressable>

					<View style={styles.onlineBadge}>
						<Image source={UserIcon} style={styles.onlineIcon} resizeMode='contain' />
						<View style={styles.onlineRow}>
							<View style={styles.liveDot} />
							<Text style={styles.onlineValue}>
								{formatBigNumbers(onlineCount ?? 0, { fractionDigits: 1 })}
							</Text>
						</View>
						<Text style={styles.onlineLabel}>
							{t('T_SITE_LOBBY_PLAYING_NOW', { defaultValue: 'playing now' })}
						</Text>
					</View>
				</View>

				<View style={styles.cardsGrid}>
					<View style={styles.cardRow}>
						{cards.slice(0, 3).map(card => renderCard(card, styles, cardScale))}
					</View>
					<View style={styles.cardRow}>
						{cards.slice(3).map(card => renderCard(card, styles, cardScale))}
					</View>
				</View>
			</View>

			{/* Outside the 390x844 canvas on purpose: the canvas is a fixed design-frame box, so a
			    bottom offset inside it would still be measured against 844, not the screen. */}
			<View style={styles.infoBars}>
				<RecentTablesBar width={horizontalScale * 360} onPress={() => navigate('MyTables')} />
			</View>
		</View>
	);
};

const renderCard = (
	card: {
		key: string;
		title: string;
		meta: string;
		icon: any;
		featured?: boolean;
		onPress: () => void;
	},
	styles: ReturnType<typeof createStyles>,
	cardScale: number,
) => (
	<CategoryCard
		key={card.key}
		onPress={card.onPress}
		style={card.featured ? styles.categoryCardFeatured : styles.categoryCard}
		radius={13.06 * cardScale}
		strokeWidth={0.87 * cardScale}
		glowUnit={cardScale}
		outerGlowOpacity={card.featured ? 0.7 : 1}
	>
		<View style={card.featured ? styles.cardContentFeatured : styles.cardContent}>
			<Image
				source={card.icon}
				style={card.featured ? styles.cardIconFeatured : styles.cardIcon}
				resizeMode='contain'
			/>
			<Text
				numberOfLines={1}
				adjustsFontSizeToFit
				minimumFontScale={0.7}
				style={card.featured ? styles.cardTitleFeatured : styles.cardTitle}
			>
				{card.title}
			</Text>
			<Text
				numberOfLines={1}
				adjustsFontSizeToFit
				minimumFontScale={0.78}
				style={card.featured ? styles.cardMetaFeatured : styles.cardMeta}
			>
				{card.meta}
			</Text>
		</View>
	</CategoryCard>
);

const getCardScale = (horizontalScale: number, viewportWidth: number) =>
	Math.min(horizontalScale, (viewportWidth - 30) / 381);

const createStyles = (
	horizontalScale: number,
	viewportWidth: number,
	infoBarsBottom: number,
	heroTop: number,
	cardsTop: number,
) => {
	const s = (value: number) => value * horizontalScale;
	const cardScale = getCardScale(horizontalScale, viewportWidth);
	const sc = (value: number) => value * cardScale;
	return StyleSheet.create({
		root: {
			position: 'absolute',
			left: 0,
			top: 0,
			right: 0,
			bottom: 0,
			width: '100%',
			alignItems: 'center',
		},
		canvas: {
			width: s(390),
			height: s(844),
			alignSelf: 'center',
			position: 'relative',
		},
		hero: {
			position: 'absolute',
			left: s(15),
			top: heroTop,
			width: s(361),
			height: s(226),
			alignItems: 'center',
		},
		playArea: {
			position: 'absolute',
			left: s(39),
			top: 0,
			width: s(276),
			height: s(226),
			alignItems: 'center',
			justifyContent: 'center',
		},
		playImage: {
			width: s(276),
			height: s(226),
		},
		onlineBadge: {
			position: 'absolute',
			right: s(1),
			top: s(88),
			width: s(70),
			height: s(70),
			borderRadius: s(35),
			borderWidth: s(1),
			borderColor: '#FFE36E',
			backgroundColor: '#430E05',
			alignItems: 'center',
			justifyContent: 'center',
		},
		onlineIcon: {
			width: s(17),
			height: s(18),
		},
		onlineRow: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(2),
			marginTop: s(2),
		},
		liveDot: {
			width: s(7),
			height: s(7),
			borderRadius: s(3.5),
			backgroundColor: '#0CEB66',
		},
		onlineValue: {
			fontFamily: 'Sofia Sans',
			fontSize: s(14),
			fontWeight: '700',
			color: '#FFFFFF',
		},
		onlineLabel: {
			fontFamily: 'Sofia Sans',
			fontSize: s(9),
			fontWeight: '500',
			color: '#FFFF99',
		},
		cardsGrid: {
			position: 'absolute',
			left: 0,
			top: cardsTop,
			width: s(390),
			alignItems: 'center',
			gap: s(7),
			overflow: 'visible',
		},
		infoBars: {
			position: 'absolute',
			left: 0,
			right: 0,
			bottom: infoBarsBottom,
			height: getRecentTablesBarHeight(s(360)),
			alignItems: 'center',
		},
		cardRow: {
			width: sc(361),
			height: sc(141),
			flexDirection: 'row',
			alignItems: 'flex-end',
			justifyContent: 'space-between',
			overflow: 'visible',
		},
		categoryCard: {
			width: sc(104),
			height: sc(130),
			position: 'relative',
			overflow: 'visible',
		},
		categoryCardFeatured: {
			width: sc(134.933),
			height: sc(141.319),
			position: 'relative',
			overflow: 'visible',
		},
		cardContent: {
			position: 'absolute',
			left: 0,
			top: 0,
			width: sc(104),
			height: sc(130),
			alignItems: 'center',
			overflow: 'hidden',
			borderRadius: sc(13),
		},
		cardContentFeatured: {
			position: 'absolute',
			left: 0,
			top: 0,
			width: sc(134.933),
			height: sc(141.319),
			alignItems: 'center',
			overflow: 'hidden',
			borderRadius: sc(13),
		},
		cardIcon: {
			position: 'absolute',
			top: sc(11),
			width: sc(72),
			height: sc(66),
			zIndex: 1,
		},
		cardIconFeatured: {
			position: 'absolute',
			top: sc(5),
			width: sc(98),
			height: sc(88),
			zIndex: 1,
		},
		cardTitle: {
			position: 'absolute',
			top: sc(80),
			fontFamily: 'Sofia Sans Semi Condensed',
			fontSize: sc(10.5),
			lineHeight: sc(19.2),
			fontWeight: '700',
			color: '#FFFFFF',
			textAlign: 'center',
			left: sc(6),
			right: sc(6),
			zIndex: 1,
		},
		cardTitleFeatured: {
			position: 'absolute',
			top: sc(94),
			fontFamily: 'Sofia Sans Semi Condensed',
			fontSize: sc(16),
			lineHeight: sc(19.2),
			fontWeight: '700',
			color: '#FFFFFF',
			textAlign: 'center',
			left: sc(7),
			right: sc(7),
			zIndex: 1,
		},
		cardMeta: {
			position: 'absolute',
			top: sc(101),
			fontFamily: 'Sofia Sans',
			fontSize: sc(10.5),
			lineHeight: sc(14.4),
			fontWeight: '500',
			color: '#FFFF99',
			textAlign: 'center',
			left: sc(6),
			right: sc(6),
			zIndex: 1,
		},
		cardMetaFeatured: {
			position: 'absolute',
			top: sc(115),
			fontFamily: 'Sofia Sans',
			fontSize: sc(12),
			lineHeight: sc(14.4),
			fontWeight: '500',
			color: '#FFFF99',
			textAlign: 'center',
			left: sc(7),
			right: sc(7),
			zIndex: 1,
		},
	});
};

export default HomeRedesignContent;
